import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { useCallback } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type {
  SalaryAdvanceListItem,
  SalaryAdvanceStatus,
} from "@/modules/hr/salary-advance/domain/contracts/types";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { useMyAdvancesQuery } from "../api/queries";
import { MyAdvanceListTable } from "../ui/MyAdvanceListTable";

const ALL_STATUS = "__all__";

export function MyAdvanceListPage() {
  const nav = useNavigate({ from: "/app/hr/salary-advance/requests" });
  const { has } = usePermissions();
  const canCreate = has(Permissions.hr_salary_advance.request_create);

  const search = useSearch({ from: "/app/hr/salary-advance/requests" }) as {
    offset?: number;
    limit?: number;
    sort?: Array<{ field: string; dir: "asc" | "desc" }>;
    status?: SalaryAdvanceStatus;
  };

  const offset = search.offset ?? 0;
  const limit = search.limit ?? 20;
  const sortBy = search.sort?.[0]?.field ?? "requestDate";
  const sortOrder = (search.sort?.[0]?.dir ?? "desc") as "asc" | "desc";

  const query = useMyAdvancesQuery({ offset, limit, status: search.status });
  const items = query.data?.data ?? [];
  const total = query.data?.meta?.total ?? 0;

  const handleStatusChange = (val: string) => {
    nav({
      search: {
        offset: 0,
        limit,
        sort: search.sort,
        status: val === ALL_STATUS ? undefined : (val as SalaryAdvanceStatus),
      },
    });
  };

  const handlePaginationChange = (newOffset: number, newLimit: number) => {
    nav({
      search: {
        offset: newOffset,
        limit: newLimit,
        sort: search.sort,
        status: search.status,
      },
    });
  };

  const handleSortingChange = useCallback(
    (id: string, desc: boolean) => {
      nav({
        search: {
          offset: 0,
          limit,
          sort: [{ field: id, dir: (desc ? "desc" : "asc") as "asc" | "desc" }],
          status: search.status,
        },
      });
    },
    [nav, limit, search.status],
  );

  const handleViewDetail = useCallback(
    (row: SalaryAdvanceListItem) => {
      nav({
        to: "/app/hr/salary-advance/requests/$id",
        params: { id: row.id },
      });
    },
    [nav],
  );

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-semibold text-2xl">ຄຳຂໍເບິກເງິນເດືອນລ່ວງໜ້າ</h1>
            <p className="text-muted-foreground text-sm">
              ລາຍການຄຳຂໍເບິກເງິນເດືອນລ່ວງໜ້າຂອງທ່ານ
            </p>
          </div>
          {canCreate && (
            <Button
              onClick={() => nav({ to: "/app/hr/salary-advance/requests/new" })}
            >
              <PlusIcon className="h-4 w-4" />
              ຍື່ນຄຳຂໍ
            </Button>
          )}
        </div>

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          {/* Filter bar */}
          <div className="mb-2 flex flex-wrap items-center gap-3 px-2">
            <Select
              value={search.status ?? ALL_STATUS}
              onValueChange={handleStatusChange}
            >
              <SelectTrigger className="w-44">
                <SelectValue placeholder="ທຸກສະຖານະ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STATUS}>ທຸກສະຖານະ</SelectItem>
                <SelectItem value="pending">ລໍຖ້າອະນຸມັດ</SelectItem>
                <SelectItem value="approved">ອະນຸມັດແລ້ວ</SelectItem>
                <SelectItem value="paid">ຈ່າຍແລ້ວ</SelectItem>
                <SelectItem value="rejected">ປະຕິເສດ</SelectItem>
                <SelectItem value="cancelled">ຍົກເລີກ</SelectItem>
                <SelectItem value="fully_repaid">ຫັກຄືນຄົບ</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <MyAdvanceListTable
            data={items}
            isLoading={query.isLoading}
            offset={offset}
            limit={limit}
            totalCount={total}
            onPaginationChange={handlePaginationChange}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortingChange={handleSortingChange}
            onViewDetail={handleViewDetail}
          />
        </div>
      </Main>
    </>
  );
}
